import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { isInternalUrl, isLegacyLink } from '../../lib/url-utils';

import { appConfigSelectors } from '../../models/app-config';
import { programSelectors } from '../../models/program';
import {
  contentSelectors,
  contentOperations,
} from '../../models/content/index.js';
import { trackContentVideoPlay } from '../../models/content/analytics';

import contentCompletion from './content-completion';
import {
  RootPatronState,
  usePatronSelector,
} from '../../common/use-patron-selector';
import { ValueType } from '../../lib/utility-types';
import { Dispatch } from 'redux';
import useIframeResize from '../../common/useIframeResize';
import { uiOperations } from '../../models/ui';
import { ID as ExternalLinkModalId } from '../external-link-modal/external-link-modal';
import { shouldShowExternalLinkWarning } from '../../lib/teams-helper';
import { Feature, getFeatureFlag } from '../../models/features/features';
import { Monitor } from './content-completion/types';
import cx from 'classnames';
import { useTrackingId } from '../../common/use-tracking-id';
import { ID as ExpiredLinkDialogId } from '../expired-link-dialog/expired-link-dialog';
import { Spinner } from '../ui';
import { useInternalLinkChecker } from '../../hooks/useInternalLinkChecker';

const getElementTagName = (obj: unknown) => {
  if (typeof obj !== 'object' || obj === null || !('tagName' in obj)) {
    return undefined;
  }

  const tagName = (obj as { tagName: unknown }).tagName;

  if (typeof tagName === 'string') return tagName;
  return undefined;
};

const isAnchorElement = (obj: unknown): obj is HTMLAnchorElement => {
  const tagName = getElementTagName(obj);

  if (!tagName) return false;
  return tagName.toUpperCase() === 'A';
};

const getParentElement = (obj: EventTarget | null) => {
  if (obj === null || !('parentElement' in obj)) return undefined;

  return (obj as { parentElement: unknown }).parentElement;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type OwnProps = {
  contentId: number;
  analyticsData: unknown;
  autoPlayVideo?: boolean;
  onResize?: () => void;
  locale?: string;
  className?: string;
};

type ContentFrameProps = StateProps & DispatchProps & OwnProps;

type VideoMessageEvent = MessageEvent<{
  event: 'start_play' | 'timeupdate' | 'paused' | 'ended';
}>;

const ContentFrame = ({
  contentId,
  programId,
  contentType,
  programPath,
  hasVideo,
  videoEmbed,
  videoUuid,
  isContentCompleted,
  setIsContentCompleted,
  openExternalLinkModal,
  openExpiredLinkDialog,
  analyticsData,
  autoPlayVideo,
  onResize,
  locale,
  className,
  displayAsVideoOnly,
}: ContentFrameProps) => {
  const iframeContentSrc = useRef<string>();
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const history = useHistory();
  const timeContentLoaded = useRef<number>(Date.now());
  const sessionUuid = useRef<string>(uuidv4());

  const newContentPage = usePatronSelector((state) =>
    getFeatureFlag(state, Feature.CONTENT_DETAIL_NEW)
  );

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const handleIframeRefChange = (node: HTMLIFrameElement | null) => {
    iframeRef.current = node;
  };

  const { isInternalLink, isLegacyLink } = useInternalLinkChecker();

  const completionMonitor = useMemo(() => {
    if (isContentCompleted) return undefined;

    const handleContentCompleted = (monitor: Monitor) => {
      if (iframeRef.current) {
        monitor.detach();
      }
      setIsContentCompleted(contentId);
    };

    const monitor = contentCompletion[contentType];
    return monitor ? new monitor(handleContentCompleted) : undefined;
  }, [contentId, contentType, isContentCompleted, setIsContentCompleted]);

  const handleInternalLinkClick = useCallback(
    (e: MouseEvent, url: URL) => {
      if (e.defaultPrevented) return;
      e.preventDefault();

      const isLegacy = isLegacyLink(url);

      const path = isLegacy
        ? `/sc4${url.hash}${url.search}` // appends provided search (query) parameter to the new path
        : url.href.split(
            [window.location.origin, programPath].filter((s) => s).join('')
          )[1];

      history.push(path);
    },
    [history, programPath, isLegacyLink]
  );

  const handleLinkClick = useCallback(
    (e: MouseEvent, target: HTMLAnchorElement) => {
      const url = new URL(target.href);
      const newTab = target.target === '_blank';
      const internalLink = isInternalLink(url);

      if (!internalLink || newTab) {
        if (shouldShowExternalLinkWarning()) {
          console.debug(
            '[content-frame] external link modal triggered by Teams detection',
            { href: target.href }
          );
          openExternalLinkModal(target.href);
          e.preventDefault();
        }

        // We don't want to capture newTab links
        return;
      }

      handleInternalLinkClick(e, url);
    },
    [handleInternalLinkClick, openExternalLinkModal, isInternalLink]
  );

  const handleIframeClick = useCallback(
    (e: MouseEvent) => {
      // for attachments, the <a> tag contains span and other elements which may be the target
      let target: HTMLAnchorElement | undefined = undefined;

      // `instanceof` doesn't work with event targets coming from the iframe for some reason
      if (isAnchorElement(e.target)) {
        target = e.target;
      } else {
        const parentElement = getParentElement(e.target);

        if (isAnchorElement(parentElement)) {
          target = parentElement;
        }
      }

      if (!target) return;

      // only handle non-attachment clicks
      if (
        !(
          target.hasAttribute('data-filetype') ||
          (target.hasAttribute('rel') && target.rel === 'attachment')
        )
      ) {
        handleLinkClick(e, target);
      } else {
        // if the link is an attachment, check if more than 5 minutes have passed since the content was loaded
        const timePassed = Date.now() - timeContentLoaded.current;
        if (timePassed > 5 * 60 * 1000) {
          // if more than 5 minutes have passed, prevent the default behavior and open the expired link dialog
          e.preventDefault();
          openExpiredLinkDialog();
          return;
        }
      }
    },
    [handleLinkClick]
  );

  useEffect(() => {
    if (!isIframeLoaded) return;

    const events: Record<string, VideoMessageEvent['data'][]> = {};
    const lastEventByKey: Record<string, string> = {};

    const persistEvent = (event: VideoMessageEvent) => {
      const key = eventKey(event);
      const previousEvent = lastEventByKey[key];

      // Reset UUID when going from ended -> timeupdate (which is a restart)
      if (previousEvent === 'ended' && event.data.event === 'timeupdate') {
        sessionUuid.current = uuidv4();
      }

      // Update the last event for this key
      lastEventByKey[key] = event.data.event;

      const eventDataWithSessionUuid = {
        ...event.data,
        sessionUuid: sessionUuid.current,
      };
      Object.prototype.hasOwnProperty.call(events, key)
        ? events[key].push(eventDataWithSessionUuid)
        : (events[key] = [eventDataWithSessionUuid]);
    };

    const lastEventTriggeredSend = (eventList: ValueType<typeof events>) => {
      const lastEventType = eventList[eventList.length - 1].event;
      return ['start_play', 'ended', 'paused'].includes(lastEventType);
    };

    const eventKey = (event: MessageEvent) => {
      return `${event.data.videoId} - ${event.data.instanceId}`;
    };

    const selfOrigin = `${window.location.protocol}//${
      window.location.hostname
    }${window.location.port ? `:${window.location.port}` : ''}`;
    const handleMessage = (e: MessageEvent) => {
      const iframe = iframeRef.current;
      if (e.origin !== selfOrigin) return;
      if (e.data.source !== 'videojs') return;
      //There could be multiple player instances, only react to message from descendant
      if (!iframe || iframe.contentWindow !== e.source) return;

      switch (e.data.event) {
        case 'start_play':
          persistEvent(e);
          trackContentVideoPlay(
            contentId,
            contentType,
            events[eventKey(e)],
            analyticsData
          );
          break;
        case 'timeupdate':
          persistEvent(e);
          break;
        case 'paused':
        case 'ended': {
          const send = !lastEventTriggeredSend(events[eventKey(e)]); // prevent double-submit from end and pause
          persistEvent(e);
          if (send)
            trackContentVideoPlay(
              contentId,
              contentType,
              events[eventKey(e)],
              analyticsData
            );
          break;
        }
      }
    };

    const iframe = iframeRef.current;
    // attach event listeners to any nested iframes (e.x. attachments)
    const allNestedFrames = Array.from(
      iframe?.contentDocument?.getElementsByTagName('iframe') ?? []
    );
    // check to ensure we can read the contentDocument
    const nestedFrames = allNestedFrames.filter((frame) => {
      try {
        return frame.contentDocument != null;
      } catch (err) {
        return false;
      }
    });

    const handleIframeLoad = () => {
      iframe?.contentDocument?.body.addEventListener(
        'click',
        handleIframeClick
      );
      nestedFrames.forEach((frame) => {
        if (frame.contentDocument?.body) {
          frame.contentDocument.body.addEventListener(
            'click',
            handleIframeClick
          );
        } else {
          frame.contentDocument?.addEventListener('load', () => {
            frame.contentDocument?.body?.addEventListener(
              'click',
              handleIframeClick
            );
          });
        }
      });
    };

    // Reattaches the event listeners within the content when the content changes
    iframe?.addEventListener('load', handleIframeLoad);
    handleIframeLoad();

    if (completionMonitor && iframe) {
      completionMonitor.attach(iframe);
    }

    if (hasVideo) {
      window.addEventListener('message', handleMessage, false);
    }

    return () => {
      // Verify presence of iframe document, as internal navigation revokes access
      if (iframe?.contentDocument && iframe.contentDocument.body) {
        iframe.contentDocument.body.removeEventListener(
          'click',
          handleIframeClick
        );
        nestedFrames.forEach((frame) =>
          frame.contentDocument?.body?.removeEventListener(
            'click',
            handleIframeClick
          )
        );
      }

      iframe?.removeEventListener('load', handleIframeLoad);

      if (completionMonitor) {
        completionMonitor.detach();
      }

      if (hasVideo) {
        window.removeEventListener('message', handleMessage, false);
        Object.values(events).forEach((event_list) => {
          // if the last event is 'ended' or 'paused', we already sent this analytics payload
          if (!lastEventTriggeredSend(event_list)) {
            trackContentVideoPlay(
              contentId,
              contentType,
              event_list,
              analyticsData
            );
          }
        });
      }
    };
  }, [
    analyticsData,
    completionMonitor,
    contentId,
    contentType,
    handleIframeClick,
    isIframeLoaded,
    hasVideo,
  ]);

  const trackingId = useTrackingId();

  const contentUrl = useMemo(() => {
    if (displayAsVideoOnly) {
      return `/embed/video/${videoUuid}?autoplay=${!!autoPlayVideo}${
        trackingId ? `&trackingId=${trackingId}` : ''
      }`;
    }

    let articleUrl = `/embed/article/${contentId}?program=${programId}${
      trackingId ? `&trackingId=${trackingId}` : ''
    }`;
    if (locale) {
      articleUrl += `&locale=${locale}`;
    }

    return articleUrl;
  }, [
    autoPlayVideo,
    contentId,
    hasVideo,
    locale,
    programId,
    videoUuid,
    trackingId,
  ]);

  const getVideoEmbedHtml = () => {
    const div = document.createElement('div');
    div.innerHTML = videoEmbed ?? '';

    const el = div.firstChild;

    if (!el || !(el instanceof HTMLElement)) return '';

    const src = el.getAttribute('src')?.replace('http://', 'https://');
    el.setAttribute('src', src ?? '');

    return el.outerHTML;
  };

  const handleFrameLoad = () => {
    setIsIframeLoaded(true);
  };

  //disabled on old content page to insulate against regressions.
  //do not resize for video-only content, css forces aspect ratio and handles precise sizing
  const disableIframeResize = displayAsVideoOnly || !newContentPage;

  const {
    height,
    ref: resizedIframeRef,
    hasResized,
  } = useIframeResize({
    onRefChange: handleIframeRefChange,
    disabled: disableIframeResize,
  });

  const onResizeRef = useRef(onResize);

  useEffect(() => {
    if (hasResized || disableIframeResize) {
      onResizeRef.current?.();
    }
  }, [hasResized, disableIframeResize]);

  // The contentUrl will primarily (though perhaps not exclusively) change
  // if the "locale" should change via the "Translate" button. In such a case,
  // we want to reload the iframe to ensure the content is updated.
  useEffect(() => {
    iframeRef.current?.contentWindow?.location?.replace(contentUrl);
  }, [contentUrl]);

  useEffect(() => {
    if (!isIframeLoaded) {
      iframeContentSrc.current = contentUrl;
    }
  }, [contentUrl, isIframeLoaded]);

  return videoEmbed && displayAsVideoOnly ? (
    <div
      className={cx('content-frame', className)}
      dangerouslySetInnerHTML={{ __html: getVideoEmbedHtml() }}
    />
  ) : (
    <div
      className={cx('content-frame', className, {
        loading: !isIframeLoaded,
      })}
    >
      <Spinner loading={!isIframeLoaded} delay />
      {/*
       * Using `scrolling="no"` to disable scrollbar on the iframe - ignore the deprecation warning since CSS
       * doesn't work unless `overflow:hidden` is added to the content body tag
       * @see https://support.moonpoint.com/network/web/html/css/iframe-obsolete-elements.php
       */}
      <iframe
        allowFullScreen
        src={iframeContentSrc.current}
        onLoad={handleFrameLoad}
        ref={resizedIframeRef}
        title={String(contentId)}
        style={
          !disableIframeResize && height !== undefined
            ? { height: `${height}px` }
            : undefined
        }
        height={disableIframeResize ? undefined : height}
        scrolling={newContentPage ? 'no' : undefined}
      />
    </div>
  );
};

const mapStateToProps = (state: RootPatronState, ownProps: OwnProps) => ({
  programPath: appConfigSelectors.getProgramPath(state),
  programId: programSelectors.getProgramId(state),
  contentType: contentSelectors.getContentType(state, ownProps),
  hasVideo: contentSelectors.getContentHasVideo(state, ownProps),
  videoEmbed: contentSelectors.getContentVideoEmbed(state, ownProps),
  videoUuid: contentSelectors.getContentVideoUuid(state, ownProps),
  isContentCompleted: contentSelectors.getContentIsCompleted(state, ownProps),
  displayAsVideoOnly: contentSelectors.getContentDisplayAsVideoOnly(
    state,
    ownProps
  ),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setIsContentCompleted: (id: number) =>
    contentOperations.completeContent(id)(dispatch),
  openExternalLinkModal: (linkUrl: string) =>
    dispatch(uiOperations.displayOverlay(ExternalLinkModalId, { linkUrl })),
  openExpiredLinkDialog: () =>
    dispatch(uiOperations.displayOverlay(ExpiredLinkDialogId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentFrame);
